<template>
  <div class="main-container">
    <div class="login-modal">
      <form id="authForm" class="login-modal__form">
        <div class="login-modal__icon" @click="hideModal">
          <svg
            class="login-modal__image svg-inline--fa"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
          >
            <path
              fill="currentColor"
              stroke="currentColor"
              stroke-linecap="square"
              stroke-width="2"
              d="M1.368 1.368l13.264 13.264m0-13.264L1.368 14.632"
            />
          </svg>
        </div>
        <h2 class="login-modal__title m-b-20">{{ headerMessage }}</h2>
        <template v-if="(isFormSubmitted && v$.$invalid) || responseWithErrors">
          <app-notifier
            v-for="(message, index) in errors"
            :key="index"
            class="checkout__error"
            :type="'failed'"
            :data="message"
          />
        </template>
        <app-input
          ref="email"
          v-model="v$.email.$model"
          placeholder=""
          label="Email"
          :has-error="v$.email.$error && isFormSubmitted"
          class="login-modal__input"
          :error-message="
            v$.email.$error && isFormSubmitted && !responseWithErrors ? validatorsCollection.get('email').errorMessage : ''
          "
        />
        <app-input
          ref="password"
          v-model="v$.password.$model"
          type="password"
          label="Password"
          :has-error="v$.password.$error && isFormSubmitted"
          class="login-modal__input"
          placeholder=""
          :error-message="
            v$.password.$error && isFormSubmitted && !responseWithErrors ? validatorsCollection.get('password').errorMessage : ''
          "
        />
        <button
          :class="{ disabled: !allFieldsComplete, 'login-modal__submit': true }"
          class="login-modal__submit"
          :disabled="!allFieldsComplete"
          @click.prevent="submit"
          @keyup.enter="submit"
        >
          Login
        </button>
        <div class="login-modal__section m-t-20 m-b-20">
          <span>
            New User?
            <app-link
              :route-to="{
                name: ROUTE_NAMES.REGISTRATION,
                params: {
                  target: $route.name,
                  vendor: getActiveVendor ? getActiveVendor.id : null
                }
              }"
              class="login-modal__link"
              @click="hideModal()"
              >Register</app-link
            >
          </span>
          <app-link :route-to="{ name: ROUTE_NAMES.FORGOT_PASSWORD }" class="login-modal__link" @click="hideModal()"
            >Forgot Password?</app-link
          >
        </div>
        <div class="separator m-t-20 m-b-20">
          <div class="separator__line">
            <hr />
          </div>
          <h4 class="separator__text">
            {{ continueAsGuest ? "OR" : "Don't Have an Account?" }}
          </h4>
          <div class="separator__line">
            <hr />
          </div>
        </div>
        <app-button
          :text="continueAsGuest ? 'Continue As Guest' : 'Register'"
          :aria-label="continueAsGuest ? 'Continue As Guest' : 'Register'"
          outlined
          class="login-modal__guest m-t-20"
          :handler="routeToNext"
        />
      </form>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import useVuelidate from "@vuelidate/core";
import { loginPasswordValidations, emailValidation, ROUTE_NAMES, REQUIRED_ERR_MSG } from "@/utils";

export default {
  name: "Login",
  props: {
    headerMessage: String
  },
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      email: "",
      password: "",
      isFormSubmitted: false,
      responseWithErrors: false,
      errors: [],
      validatorsCollection: new Map(),
      remember: [],
      continueAsGuest: true,
      ROUTE_NAMES
    };
  },
  computed: {
    ...mapGetters("vendor", ["getActiveVendor"]),
    ...mapActions("user", ["fetchFavoriteItemsIds"]),
    rememberUser: function () {
      return this.remember.length > 0;
    },
    allFieldsComplete: function () {
      return this.v$.email.$model !== "" && this.v$.password.$model !== "";
    }
  },
  validations: {
    email: emailValidation(),
    password: loginPasswordValidations()
  },
  methods: {
    ...mapActions("user", ["authorization"]),
    submit() {
      this.isFormSubmitted = false;
      this.responseWithErrors = false;
      this.errors.length = 0;
      this.v$.$touch();
      this.checkInvalidFields();
      this.isFormSubmitted = true;

      if (!this.v$.$invalid) {
        return this.authorization({
          email: this.email,
          password: this.password,
          remember: this.rememberUser
        }).then(
          () => {
            this.hideModal();
            this.fetchFavoriteItemsIds;
          },
          (error) => {
            if (error.status === 401 || error.status === 404) {
              this.errors.push("Invalid login. Please enter a valid email address and password combination");
            } else {
              this.errors.push("Login failed. Please try again later.");
            }
            this.responseWithErrors = true;
          }
        );
      }

      return true;
    },
    checkInvalidFields() {
      this.validatorsCollection.forEach((item) => {
        if (item.validator.$invalid) {
          if (item.validator.required.$invalid) {
            if (!this.errors.includes(REQUIRED_ERR_MSG)) {
              this.errors.push(REQUIRED_ERR_MSG);
            }
          }
        }
      });
    },
    hideModal() {
      this.$modal.hide("login");
    },
    routeToNext() {
      const pathName = this.continueAsGuest ? ROUTE_NAMES.CONTINUE_AS_GUEST : ROUTE_NAMES.REGISTRATION;
      this.$router.push({
        name: pathName,
        params: {
          target: this.$route.params.target || null
        }
      });
    }
  },
  beforeMount() {
    this.validatorsCollection
      .set("email", {
        validator: this.v$.email,
        errorMessage: "Please enter a valid email address"
      })
      .set("password", {
        validator: this.v$.password,
        errorMessage: "Please enter at least 8 characters"
      });
    this.$refs.email?.$el?.focus();
  }
};
</script>

<style lang="scss">
.dark {
  .login-modal {
    color: $primary-color;
  }
}
.main-container {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.separator {
  display: flex;
  align-items: center;
  width: 100%;

  &__line {
    flex: 1 30%;
    hr {
      opacity: 0.3;
      border: solid 1px $group-3-color-3;
    }
  }

  &__text {
    flex: 2;
    text-align: center;
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.43;
  }
}
.login-modal {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: auto;
  width: 100%;
  height: 100%;
  font-family: $fontFamilyPrimary;
  color: $group-3-color-2;

  &__title {
    width: 100%;
    text-align: left;
    font-size: 24px;
    font-weight: 100;
    font-weight: bold;
    margin-top: 73px;
    color: $group-3-color-2;
  }

  &__form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 393px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    margin-left: $spacer;
    margin-right: $spacer;

    @include respond-to-mobile {
      width: 100%;
    }
  }

  &__section {
    display: flex;
    justify-content: space-between;
    width: 90%;
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.43;
    letter-spacing: normal;

    @include respond-to-mobile {
      justify-content: space-between;
    }
  }

  &__submit {
    margin-top: 40px;
    width: 100%;
    min-height: 46px;
    border-radius: 34.5px;
    border: 0;
    background: none;
    box-shadow: none;
    background-color: $group-3-color-4;

    font-size: 14px;
    font-weight: 500;
    text-align: center;

    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.43;
    letter-spacing: normal;
    color: $group-3-color-1;
  }

  &__guest {
    height: 46px !important;
    color: $group-3-color-2 !important;
    border-radius: 23px !important;
    border: solid 1.5px $group-3-color-4 !important;
  }

  &__link {
    color: $group-3-color-4 !important;
    padding-left: (calc($spacer / 4));
  }

  &__icon {
    cursor: pointer;
    position: absolute;
    top: 35px;
    left: $spacer;

    img {
      width: 20px;
      height: 20px;
    }
  }
}

.disabled {
  opacity: 0.5;
}
</style>
